import React, { useState } from "react";
import labels from "../data/labels";
import wordlist_de from "../data/wordlist.de.json";

const flattenWords = (obj) =>
  Object.entries(obj).flatMap(([category, arr]) => arr.map(word => ({ translated: word, category })));

const DIFFICULTY_CATEGORIES = {
  easy: ["core_words", "family", "colors", "animals", "food_and_drink"],
  medium: ["home", "school", "toys", "clothing"],
  hard: ["tools", "transport", "technology", "nature"],
};

function filterWordsByDifficulty(words, difficulty) {
  return words.filter(wordObj => {
    const word = wordObj.translated || wordObj;
    const cat = wordObj.category || "other";
    const len = word.length;
    if (difficulty === "easy") {
      return len <= 4 && DIFFICULTY_CATEGORIES.easy.includes(cat);
    }
    if (difficulty === "medium") {
      return len >= 5 && len <= 7 && DIFFICULTY_CATEGORIES.medium.includes(cat);
    }
    if (difficulty === "hard") {
      return len >= 8 && DIFFICULTY_CATEGORIES.hard.includes(cat);
    }
    return true;
  });
}

const FlashCard = ({ words, language, onScore, onViewResults, category }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentWord, setCurrentWord] = useState({});
  const [wordIndex, setWordIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [duration, setDuration] = useState(1000);
  const [showWord, setShowWord] = useState(false);
  const [difficulty, setDifficulty] = useState(null);

  const showNextWord = (word) => {
    setCurrentWord(word);
    setShowWord(true);
    setTimeout(() => setShowWord(false), duration);
  };

  const startGame = (selectedDifficulty) => {
    let wordPool;
    if (Array.isArray(words) && words.length && typeof words[0] === 'object') {
      wordPool = words;
    } else if (language === "de") {
      wordPool = flattenWords(wordlist_de);
    } else {
      wordPool = flattenWords(words);
    }
    // Filter by category if provided
    if (category) {
      wordPool = wordPool.filter(w => w.category === category);
    }
    if (selectedDifficulty) {
      wordPool = filterWordsByDifficulty(wordPool, selectedDifficulty);
    }
    for (let i = wordPool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [wordPool[i], wordPool[j]] = [wordPool[j], wordPool[i]];
    }
    const selected = wordPool.slice(0, 10);
    setRound(selected);
    setWordIndex(0);
    setScore(0);
    setShowResult(false);
    setIsRunning(true);
    setDifficulty(selectedDifficulty);
    showNextWord(selected[0]);
  };

  const handleFeedback = (correct) => {
    if (correct) setScore((prev) => prev + 1);
    const nextIndex = wordIndex + 1;
    if (nextIndex < round.length) {
      setWordIndex(nextIndex);
      showNextWord(round[nextIndex]);
    } else {
      setIsRunning(false);
      setShowResult(true);
      if (onScore) onScore(score);
    }
  };

  // Compute available difficulties for the current category
  const getAvailableDifficulties = () => {
    let wordPool;
    if (Array.isArray(words) && words.length && typeof words[0] === 'object') {
      wordPool = words;
    } else if (language === "de") {
      wordPool = flattenWords(wordlist_de);
    } else {
      wordPool = flattenWords(words);
    }
    if (category) {
      wordPool = wordPool.filter(w => w.category === category);
    }
    const difficulties = ['easy', 'medium', 'hard'];
    const available = {};
    difficulties.forEach(diff => {
      const filtered = filterWordsByDifficulty(wordPool, diff);
      available[diff] = filtered.length >= 10;
    });
    return available;
  };

  const availableDifficulties = getAvailableDifficulties();
  const allUnavailable = !availableDifficulties.easy && !availableDifficulties.medium && !availableDifficulties.hard;
  const someUnavailable = Object.values(availableDifficulties).some(v => !v);

  // Difficulty selection UI
  if (!isRunning && !showResult && !difficulty) {
    return (
      <div style={{ width: '100%', maxWidth: 420, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <label style={{ fontSize: '1.3em', marginBottom: 12, textAlign: 'center' }}>{labels[language].chooseDifficulty || "Choose difficulty:"}</label>
        {availableDifficulties.easy && (
          <button className="green-button" style={{ fontSize: '1.3rem', padding: '18px 0', width: '100%' }} onClick={() => setDifficulty('easy')}>{labels[language].difficultyEasy || 'Easy'}</button>
        )}
        {availableDifficulties.medium && (
          <button className="green-button" style={{ fontSize: '1.3rem', padding: '18px 0', width: '100%' }} onClick={() => setDifficulty('medium')}>{labels[language].difficultyMedium || 'Medium'}</button>
        )}
        {availableDifficulties.hard && (
          <button className="green-button" style={{ fontSize: '1.3rem', padding: '18px 0', width: '100%' }} onClick={() => setDifficulty('hard')}>{labels[language].difficultyHard || 'Hard'}</button>
        )}
        {someUnavailable && !allUnavailable && (
          <div style={{ color: '#f44336', marginTop: 8, textAlign: 'center', fontSize: '1.1em' }}>
            {labels[language].notEnoughWords || "We don't have yet enough words for the other difficulties."}
          </div>
        )}
        {allUnavailable && (
          <div style={{ color: '#f44336', marginTop: 8, textAlign: 'center', fontSize: '1.1em' }}>
            {labels[language].noWords || "No words available for this category."}
          </div>
        )}
      </div>
    );
  }

  // After difficulty is chosen, show time selection and start
  if (!isRunning && !showResult && difficulty) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 16, width: '100%' }}>
        <label style={{ fontSize: '1.3em', marginBottom: 12, display: 'block', textAlign: 'center' }}>
          {labels[language].durationLabel || "Show word for (seconds)"}: {" "}
          <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} style={{ fontSize: '1.2em', padding: '8px 16px', marginLeft: 8 }}>
            <option value={500}>0.5s</option>
            <option value={1000}>1s</option>
            <option value={1500}>1.5s</option>
            <option value={2000}>2s</option>
            <option value={2500}>2.5s</option>
            <option value={3000}>3s</option>
          </select>
        </label>
        <button onClick={() => startGame(difficulty)} className="green-button" style={{ fontSize: '1.3rem', padding: '18px 36px', width: '100%' }}>{labels[language].start || "Start"}</button>
        <button onClick={() => setDifficulty(null)} className="small-button" style={{ width: '100%' }}>{labels[language].backToGame || 'Back to Game'}</button>
      </div>
    );
  }

  // Guard: if round is empty, show a message
  if ((isRunning || showResult) && round.length === 0) {
    return (
      <div className="flashcard" style={{ textAlign: 'center', padding: 32 }}>
        <h2>{labels[language].noWords || "No words available for this category and difficulty."}</h2>
        <button onClick={() => { setDifficulty(null); setShowResult(false); setIsRunning(false); }} className="green-button" style={{ marginTop: 24 }}>
          {labels[language].backToGame || 'Back to Game'}
        </button>
      </div>
    );
  }

  return (
    <div className="flashcard">
      {isRunning && (
        <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 0 }}>
          <div style={{ width: '100%', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 0, padding: 0 }}>
            {showWord ? (
              <h1 style={{
                width: '100%',
                margin: 0,
                fontWeight: 700,
                fontSize: 'min(16vw, 8vh, 4.5em)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'center',
                letterSpacing: '0.03em',
                lineHeight: 1.1
              }}>{currentWord.translated}</h1>
            ) : (
              <div className="feedback-buttons">
                <button onClick={() => handleFeedback(true)} className="large-button">✅</button>
                <button onClick={() => handleFeedback(false)} className="large-button">❌</button>
              </div>
            )}
          </div>
          <p style={{ textAlign: 'center' }}>
            {labels[language].word || "Word"} {wordIndex + 1} {labels[language].of || "of"} {round.length}
          </p>
        </div>
      )}
      {showResult && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <h2>{labels[language].result || "Result"}</h2>
          <p>{labels[language].correctRead || "Correctly read"}: {score} {labels[language].of || "of"} {round.length}</p>
          {score >= 8 && <h3>🎉 {labels[language].amazing || "Amazing!"}</h3>}
          {score >= 5 && score < 8 && <h3>😊 {labels[language].good || "Good Game"}</h3>}
          {score >= 3 && score < 5 && <h3>😐 {labels[language].couldDoBetter || "You could do better"}</h3>}
          {score < 3 && <h3>😢 {labels[language].tryAgain || "Please try again"}</h3>}
          <button onClick={() => { setDifficulty(null); setShowResult(false); }} className="green-button" style={{ marginTop: 24 }}>
            {labels[language].newGame || "New Game"}
          </button>
          <button onClick={onViewResults} className="small-button" style={{ marginTop: 16 }}>
            {labels[language].viewResults || "View Results"}
          </button>
        </div>
      )}
    </div>
  );
};

export default FlashCard;